import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { useApiHandlers } from 'lib/reader';
import {
  Alert,
  ContentBox,
  LoadingIndicator,
  RichTextValue
} from 'components/common';
import {
  ReaderBlockDiscussionActions,
  ReaderBlockDiscussionCommentWrapper,
  ReaderBlockDiscussionHeader,
  ReaderBlockDiscussionShowMore
} from '.';

import type {
  DiscussionComment as IDiscussionComment,
  ServerConfig,
  UserReactions,
  UserWithBasicData
} from 'shared-types';
import type { CommonBlockPropsReader } from 'components/reader/blocks';

const INITIAL_COMMENTS_VISIBLE = 5;
const SHOW_MORE_COMMENTS = 20;

export type DiscussionComment = IDiscussionComment & {
  parentCommentId: number;
  reactions: UserReactions;
  user: UserWithBasicData;
};

export type DiscussionSubmitObj = Pick<
  IDiscussionComment,
  'attachment' | 'attachmentType' | 'text'
> & { isAnonymous?: boolean; courseLanguageVersion?: string } & (
    | { commentId: number; parentCommentId?: never }
    | { commentId?: never; parentCommentId: number }
  );

interface State {
  childCommentsOrders: Record<string, number[]>;
  childCommentsShowAfterIndex: Record<string, number>;
  childCommentsShowReplies: Record<string, boolean>;
  comments: Record<string, DiscussionComment>;
  commentsDisplayOrder: ServerConfig['discussionDefaultCommentsOrder'];
  commentsOrder: number[];
  commentsShowAfterIndex: number;
  commentsVisible: number;
  error: boolean;
  lastCommentId: number;
  loading: boolean;
}

const ReaderBlockDiscussion = ({
  blockId,
  data,
  font,
  fontSize
}: CommonBlockPropsReader<'discussion'>) => {
  const {
    messages,
    pageState: { completion, readOnly },
    serverConfig: { discussionDefaultCommentsOrder },
    theme
  } = useReduxState();

  const {
    onDiscussionCommentEdit: edit,
    getDiscussionComments: get,
    onDiscussionCommentDelete: remove,
    onDiscussionCommentSubmit: submit
  } = useApiHandlers();

  const { query } = useRouter();

  const [state, setState] = useState<State>({
    childCommentsOrders: {},
    childCommentsShowAfterIndex: {},
    childCommentsShowReplies: {},
    comments: {},
    commentsDisplayOrder: discussionDefaultCommentsOrder,
    commentsOrder: [],
    commentsShowAfterIndex: null,
    commentsVisible: INITIAL_COMMENTS_VISIBLE,
    error: false,
    lastCommentId: null,
    loading: true
  });

  useEffect(() => {
    getInitialComments();
  }, []);

  const getInitialComments = async () => {
    if (readOnly || !get) return;
    setState({ ...state, loading: true });

    // filter by language version in the backend
    let filterLanguage = completion?.courseLanguageVersion;
    if (!filterLanguage && typeof query.languageVersion === 'string') {
      filterLanguage = query.languageVersion;
    }

    const commentsData = await get({
      blockId,
      languageVersion: filterLanguage
    });

    if (commentsData) {
      let commentsVisible = INITIAL_COMMENTS_VISIBLE;
      if (query.parentCommentId) {
        const index = commentsData.findIndex(
          (comment) =>
            comment.id === parseInt(query.parentCommentId as string, 10)
        );

        commentsVisible =
          index >= commentsVisible ? index + 1 : commentsVisible;
      }

      updateStateWithCommentsData({
        commentsData,
        commentsVisible,
        loading: false
      });
    } else setState({ ...state, error: true, loading: false });
  };

  const handleDisplayOrderChange = (
    value: typeof discussionDefaultCommentsOrder
  ) =>
    setState({
      ...state,
      commentsDisplayOrder: value,
      commentsOrder: [...state.commentsOrder.reverse()]
    });

  const updateStateWithCommentsData = ({
    commentsData,
    ...additionalState
  }: Partial<typeof state> & { commentsData: DiscussionComment[] }) => {
    const comments = { ...state.comments };
    const commentsOrder = [...state.commentsOrder];
    const childCommentsOrders = { ...state.childCommentsOrders };
    const childCommentsShowReplies = {
      ...state.childCommentsShowReplies
    };
    let lastCommentId = state.lastCommentId;

    for (let i = 0; i < commentsData.length; i++) {
      const comment = commentsData[i];
      comments[comment.id] = comment;

      if (comment.parentCommentId) {
        if (!childCommentsOrders[comment.parentCommentId]) {
          childCommentsOrders[comment.parentCommentId] = [];
        }
        childCommentsOrders[comment.parentCommentId] = [
          ...childCommentsOrders[comment.parentCommentId],
          comment.id
        ];
      } else {
        if (state.commentsDisplayOrder === 'oldest')
          commentsOrder.push(comment.id);
        else commentsOrder.splice(0, 0, comment.id);

        childCommentsOrders[comment.id] = [];
        childCommentsShowReplies[comment.id] = false;
      }
      lastCommentId = comment.id;
    }

    setState({
      ...state,
      childCommentsOrders,
      childCommentsShowReplies,
      comments,
      commentsOrder,
      lastCommentId,
      ...additionalState
    });
  };

  const saveChanges = async ({
    attachment,
    attachmentType,
    commentId,
    text
  }: DiscussionSubmitObj) => {
    const comment = await edit(commentId, { attachment, attachmentType, text });
    setState({
      ...state,
      comments: { ...state.comments, [commentId]: comment }
    });
  };

  const submitComment = async ({
    attachment,
    attachmentType,
    isAnonymous,
    parentCommentId,
    text
  }: DiscussionSubmitObj) => {
    const success = await submit({
      attachment,
      attachmentType,
      blockId,
      courseLanguageVersion: completion?.courseLanguageVersion,
      isAnonymous,
      lastCommentId: state.lastCommentId,
      parentCommentId,
      text
    });
    if (!success) return;

    // get all new comments that have been posted after component mount
    let filterLanguage = completion?.courseLanguageVersion;
    if (!filterLanguage && typeof query.languageVersion === 'string') {
      filterLanguage = query.languageVersion;
    }

    const comments = await get({
      blockId,
      fromId: state.lastCommentId,
      languageVersion: filterLanguage
    });

    updateStateWithCommentsData({
      commentsData: comments,
      ...(parentCommentId
        ? {
            childCommentsShowAfterIndex: {
              ...state.childCommentsShowAfterIndex,
              [parentCommentId]:
                !state.childCommentsShowReplies[parentCommentId] &&
                !state.childCommentsShowAfterIndex[parentCommentId] &&
                state.childCommentsOrders[parentCommentId].length
                  ? state.childCommentsOrders[parentCommentId].length - 1
                  : state.childCommentsShowAfterIndex[parentCommentId]
            },
            ...(state.childCommentsOrders[parentCommentId].length
              ? {}
              : {
                  childCommentsShowReplies: {
                    ...state.childCommentsShowReplies,
                    [parentCommentId]: true
                  }
                })
          }
        : {
            commentsShowAfterIndex:
              !state.commentsShowAfterIndex &&
              state.commentsOrder.length > state.commentsVisible
                ? state.commentsOrder.length - 1
                : state.commentsShowAfterIndex,
            ...(state.commentsOrder.length === state.commentsVisible
              ? { commentsVisible: state.commentsVisible + 1 }
              : {})
          })
    });
  };

  const deleteComment = async (
    commentId: number,
    parentCommentId?: number,
    deletionReason?: string
  ) => {
    const success = await remove(commentId, deletionReason);
    if (!success) return;

    // update state
    const { [commentId]: deletedComment, ...comments } = state.comments;

    const index = parentCommentId
      ? state.childCommentsOrders[parentCommentId].indexOf(commentId)
      : state.commentsOrder.indexOf(commentId);

    setState({
      ...state,
      comments,
      ...(parentCommentId
        ? {
            childCommentsOrders: {
              ...state.childCommentsOrders,
              [parentCommentId]: [
                ...state.childCommentsOrders[parentCommentId].slice(0, index),
                ...state.childCommentsOrders[parentCommentId].slice(index + 1)
              ]
            }
          }
        : {
            commentsOrder: [
              ...state.commentsOrder.slice(0, index),
              ...state.commentsOrder.slice(index + 1)
            ],
            ...(state.commentsOrder.length === state.commentsVisible
              ? { commentsVisible: state.commentsVisible - 1 }
              : {})
          })
    });
  };

  const toggleReplies = ({ parentCommentId }: { parentCommentId: number }) => {
    // show or hide all child comments
    setState({
      ...state,
      childCommentsShowReplies: {
        ...state.childCommentsShowReplies,
        [parentCommentId]: state.childCommentsShowReplies[parentCommentId]
          ? false
          : true
      },
      childCommentsShowAfterIndex: {
        ...state.childCommentsShowAfterIndex,
        [parentCommentId]: null
      }
    });
  };

  const showLessComments = () => {
    setState({ ...state, commentsVisible: INITIAL_COMMENTS_VISIBLE });
  };

  const showMoreComments = () => {
    const newVisible = Math.min(
      state.commentsVisible + SHOW_MORE_COMMENTS,
      state.commentsOrder.length
    );
    setState({
      ...state,
      commentsVisible: newVisible,
      commentsShowAfterIndex:
        newVisible >= state.commentsOrder.length
          ? null
          : state.commentsShowAfterIndex
    });
  };

  return (
    <div className="discussion">
      <ContentBox
        size={fontSize}
        title={data?.title || messages.readerDiscussion}
        titleFont={font}
      >
        <div className="question" style={{ fontFamily: font }}>
          <RichTextValue html={data?.text} />
        </div>

        {readOnly ? (
          <div className="error">
            <Alert text={messages.readerDiscussionReadOnly} type="info-dark" />
          </div>
        ) : state.loading ? (
          <LoadingIndicator />
        ) : state.error ? (
          <div className="error">
            <Alert text={messages.readerDiscussionError} type="error-light" />
          </div>
        ) : (
          <>
            <div className="comments">
              {state?.commentsOrder?.length ? (
                <div className="comments-header">
                  <ReaderBlockDiscussionHeader
                    commentDisplayOrder={state.commentsDisplayOrder}
                    onOrderChange={handleDisplayOrderChange}
                  />
                </div>
              ) : null}

              {state.commentsOrder
                .filter((commentId, i) => i < state.commentsVisible)
                .map((commentId) => (
                  <ReaderBlockDiscussionCommentWrapper
                    key={commentId}
                    allowAnonymousComments={data?.allowAnonymousComments}
                    childCommentsOrder={state.childCommentsOrders[commentId]}
                    childCommentsShowReplies={
                      state.childCommentsShowReplies[commentId]
                    }
                    childCommentsShowAfterIndex={
                      state.childCommentsShowAfterIndex[commentId]
                    }
                    comment={state.comments[commentId]}
                    comments={state.comments}
                    font={font}
                    fontSize={fontSize}
                    onDelete={deleteComment}
                    onSaveChanges={saveChanges}
                    onSubmit={submitComment}
                    onToggleReplies={toggleReplies}
                  />
                ))}

              <ReaderBlockDiscussionShowMore
                count={
                  state.commentsShowAfterIndex
                    ? state.commentsShowAfterIndex + 1 - state.commentsVisible
                    : state.commentsOrder.length - state.commentsVisible
                }
                fontSize={fontSize}
                onLessClick={showLessComments}
                onMoreClick={showMoreComments}
                showLess={state.commentsVisible > INITIAL_COMMENTS_VISIBLE}
              />

              {state.commentsOrder
                .filter(
                  (commentId, i) =>
                    state.commentsShowAfterIndex &&
                    i > state.commentsShowAfterIndex
                )
                .map((commentId) => (
                  <ReaderBlockDiscussionCommentWrapper
                    key={commentId}
                    allowAnonymousComments={data?.allowAnonymousComments}
                    childCommentsOrder={state.childCommentsOrders[commentId]}
                    childCommentsShowReplies={
                      state.childCommentsShowReplies[commentId]
                    }
                    childCommentsShowAfterIndex={
                      state.childCommentsShowAfterIndex[commentId]
                    }
                    comment={state.comments[commentId]}
                    comments={state.comments}
                    font={font}
                    fontSize={fontSize}
                    onDelete={deleteComment}
                    onSaveChanges={saveChanges}
                    onSubmit={submitComment}
                    onToggleReplies={toggleReplies}
                  />
                ))}
            </div>

            <ReaderBlockDiscussionActions
              allowAnonymousComments={data?.allowAnonymousComments}
              fontSize={fontSize}
              onCommentAdd={submitComment}
            />
          </>
        )}
      </ContentBox>

      <style jsx>{`
        .question {
          color: ${theme.textColorLight};
        }
        .error {
          margin-top: 15px;
        }
        .comments {
          margin-top: 30px;
        }
        .comment-wrapper {
          margin-top: 20px;
        }
        .child-comments,
        .child-form {
          margin-left: 10%;
        }
      `}</style>
    </div>
  );
};

export default ReaderBlockDiscussion;
